// Layer 3 (Transformation) — crew and clock-window cuts of unplanned events.
// Pure projections over the shared maintenance event log.

import type { CrewId } from '@/versions/v1/schema/seat';
import { BUNKER_HILL_CREWS } from '@/versions/v1/shared/bunker-hill-org-seed';
import { eventCrewId, eventsByKind, type DowntimeEvent } from '../maintenance/DowntimeEvents.transform';
import { SHIFT_CAUSE_GROUP } from './ShiftCorrelation.seed';
import { valueLostForEvent } from './ValueLost.transform';

export type CrewEventRow = {
  crewId: CrewId;
  panel: string;
  eventCount: number;
  hours: number;
  processCount: number;
  equipmentCount: number;
  dollars: number;
  sharePct: number;
};

export type ShiftWindowRow = {
  startHour: 0 | 6 | 12 | 18;
  label: string;
  eventCount: number;
  hours: number;
  sharePct: number;
};

export type ShiftCorrelationRead = {
  crews: CrewEventRow[];
  windows: ShiftWindowRow[];
  totalEvents: number;
  /** Mean unplanned event count across the rostered crews. */
  averagePerCrew: number;
  busiestCrew: CrewEventRow;
  peakWindow: ShiftWindowRow;
  worstVsField: { crew: CrewEventRow; othersMeanSharePct: number; ratio: number };
  hypotheses: { label: string; evidence: string }[];
};

/** Event count, hours, process and equipment counts for all four crews, in A–D order. */
export function eventsByCrew(rows: readonly DowntimeEvent[]): CrewEventRow[] {
  const unplanned = eventsByKind(rows).unplanned;
  const totals = new Map<CrewId, { eventCount: number; hours: number; processCount: number; equipmentCount: number; dollars: number }>(
    BUNKER_HILL_CREWS.map((crew) => [crew.id, { eventCount: 0, hours: 0, processCount: 0, equipmentCount: 0, dollars: 0 }]),
  );
  for (const event of unplanned) {
    const total = totals.get(eventCrewId(event))!;
    total.eventCount += 1;
    total.hours += event.hours;
    total.dollars += valueLostForEvent(event);
    const group = SHIFT_CAUSE_GROUP[event.causeCategory];
    if (group === 'process') total.processCount += 1;
    else total.equipmentCount += 1;
  }
  return BUNKER_HILL_CREWS.map((crew) => {
    const total = totals.get(crew.id)!;
    const suffix = crew.id.slice(-1).toUpperCase();
    return {
      crewId: crew.id,
      panel: `Panel ${suffix}`,
      eventCount: total.eventCount,
      hours: total.hours,
      processCount: total.processCount,
      equipmentCount: total.equipmentCount,
      dollars: total.dollars,
      sharePct: unplanned.length > 0 ? (total.eventCount / unplanned.length) * 100 : 0,
    };
  });
}

/** Four six-hour start windows in Paul's day-shift-first order, retaining zero rows. */
export function eventsByWindow(rows: readonly DowntimeEvent[]): ShiftWindowRow[] {
  const unplanned = eventsByKind(rows).unplanned;
  const order = [6, 12, 18, 0] as const;
  const totals = new Map<number, { eventCount: number; hours: number }>(
    order.map((hour) => [hour, { eventCount: 0, hours: 0 }]),
  );
  for (const event of unplanned) {
    const startHour = Math.floor(Number(event.start.slice(11, 13)) / 6) * 6;
    const total = totals.get(startHour)!;
    total.eventCount += 1;
    total.hours += event.hours;
  }
  return order.map((startHour) => {
    const total = totals.get(startHour)!;
    return {
      startHour,
      label: `${String(startHour).padStart(2, '0')}:00–${String(startHour + 6).padStart(2, '0')}:00`,
      eventCount: total.eventCount,
      hours: total.hours,
      sharePct: unplanned.length > 0 ? (total.eventCount / unplanned.length) * 100 : 0,
    };
  });
}

/** Cross-tab of unplanned event counts by crew and six-hour start window. */
export function eventsByCrewAndWindow(rows: readonly DowntimeEvent[]): ReadonlyMap<CrewId, ReadonlyMap<number, number>> {
  const totals = new Map<CrewId, Map<number, number>>(
    BUNKER_HILL_CREWS.map((crew) => [crew.id, new Map([[0, 0], [6, 0], [12, 0], [18, 0]])]),
  );
  for (const event of eventsByKind(rows).unplanned) {
    const hour = Math.floor(Number(event.start.slice(11, 13)) / 6) * 6;
    const crew = eventCrewId(event);
    const counts = totals.get(crew)!;
    counts.set(hour, (counts.get(hour) ?? 0) + 1);
  }
  return totals;
}

/** Four summary reads and only the hypotheses the current seed can support. */
export function shiftCorrelationRead(rows: readonly DowntimeEvent[]): ShiftCorrelationRead {
  const totalEvents = eventsByKind(rows).unplanned.length;
  const crews = eventsByCrew(rows);
  const windows = eventsByWindow(rows);
  const busiestCrew = crews.reduce((best, row) => row.eventCount > best.eventCount ? row : best);
  const peakWindow = windows.reduce((best, row) => row.eventCount > best.eventCount ? row : best);
  const worstVsFieldCrew = crews.reduce((best, row) => row.eventCount > best.eventCount ? row : best);
  const othersMeanSharePct = crews.filter((row) => row.crewId !== worstVsFieldCrew.crewId)
    .reduce((sum, row) => sum + row.sharePct, 0) / Math.max(1, crews.length - 1);
  const ratio = othersMeanSharePct > 0 ? worstVsFieldCrew.sharePct / othersMeanSharePct : 0;
  const crossTab = eventsByCrewAndWindow(rows);
  const inPeakWindow = crossTab.get(busiestCrew.crewId)?.get(peakWindow.startHour) ?? 0;
  const processShare = crews.map((row) => ({
    row,
    share: row.eventCount > 0 ? row.processCount / row.eventCount : 0,
  })).sort((a, b) => b.share - a.share || a.row.panel.localeCompare(b.row.panel))[0];
  const hypotheses = [
    totalEvents > 0 && busiestCrew.eventCount > 0 && peakWindow.eventCount > 0
      ? { label: `${busiestCrew.panel} overlaps the peak ${peakWindow.label} window`, evidence: `${inPeakWindow}/${busiestCrew.eventCount} panel events · ${Math.round(peakWindow.sharePct)}% of all events` }
      : null,
    processShare !== undefined && processShare.row.processCount > 0 && processShare.row.equipmentCount > 0
      ? { label: `${processShare.row.panel} has the highest process share`, evidence: `${processShare.row.processCount} process · ${processShare.row.equipmentCount} equipment` }
      : null,
  ].filter((row): row is { label: string; evidence: string } => row !== null);
  return {
    crews,
    windows,
    totalEvents,
    averagePerCrew: crews.length > 0 ? totalEvents / crews.length : 0,
    busiestCrew,
    peakWindow,
    worstVsField: { crew: worstVsFieldCrew, othersMeanSharePct, ratio },
    hypotheses,
  };
}
